import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "appliance-repair-web-design-colorado",
  metaTitle: "Appliance Repair Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom appliance repair web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Appliance Repair Web Design Colorado",
  eyebrow: "Appliance Repair Web Design — Colorado",
  h1: (
    <>
      Appliance Repair <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded appliance repair websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Appliance Repair websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first appliance repair sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Appliance Repair web design in Colorado",
    body: "Appliance Repair web design is the practice of building conversion-focused, mobile-first websites for appliance repair businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — appliance breakdowns are same-day calls." },
    { title: "Appliance-type service pages", body: "Refrigerator, washer, dryer, oven, dishwasher, microwave — each ranks independently." },
    { title: "Brand pages", body: "Sub-Zero, Wolf, Viking, Samsung, LG, Whirlpool — branded pages capture branded search." },
    { title: "Same-day booking", body: "Real-time tech availability and same-day booking above the fold." },
    { title: "Sticky click-to-call", body: "Phone on every scroll." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Branded + appliance-type queries dominate", body: "\"Sub-Zero repair\", \"LG washer repair\" are higher-intent than \"appliance repair near me\"." },
    { heading: "Same-day messaging wins the call", body: "Tech availability + same-day booking above the fold beats the next listing." },
    { heading: "Brand certifications drive premium calls", body: "Sub-Zero/Wolf/Viking certified pages capture high-ticket premium-appliance owners." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a appliance repair website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a appliance repair website?", a: "Typical timeline is 4–6 weeks for a 12–20 page appliance repair site with full service and service-area pages. Larger multi-location appliance repair businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs appliance repair businesses?", a: "No — we build appliance repair sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Appliance Repair Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for appliance repair businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/appliance-repair-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
